import type {
    AgentAnswerRecap,
    AgentChatEntry,
    AgentConversation,
    AgentQuestion,
    AgentQuestionAnswer,
    AgentQuestionnaire,
    AgentTurn,
    AgentTurnRequest
} from './types';

/** Whether this turn applied approved changes to the catalog. */
export const turnAppliedChanges = (turn: AgentTurn) =>
    Boolean(
        turn.hasExternalChanges ||
        turn.resultRecap?.state === 'applied' ||
        turn.resultRecap?.items.some((item) => item.code.startsWith('apply_') && item.state === 'applied')
    );

export const publishedResultEntries = (turn: AgentTurn): AgentChatEntry[] => {
    if (!turn.turnId || !turn.hasDraftSnapshot || turn.resultNumber == null) return [];
    const published = turn.publishedDrafts?.filter((draft) => draft.publishedInTurn) ?? [];
    if (published.length > 0)
        return published.map((draft) => ({
            id: `draft-result-${turn.turnId}-${draft.draftId}`,
            role: 'draft-result' as const,
            turnId: turn.turnId!,
            resultNumber: draft.resultNumber,
            kind: draft.kind,
            draftId: draft.draftId,
            title: draft.title,
            revisionNumber: draft.revisionNumber
        }));
    return [];
};

export const hasQuestionnaireChoices = (questionnaire?: AgentQuestionnaire | null) =>
    !!questionnaire?.questions.some((question) => question.type !== 'text' && question.options.length > 0);

export const followsWidgetAnswers = (entries: AgentChatEntry[], index: number) => {
    for (let previous = index - 1; previous >= 0; previous--) {
        const entry = entries[previous];
        if (entry.role === 'notice' || entry.role === 'draft-result') continue;
        return entry.role === 'merchant' && !!entry.answers;
    }
    return false;
};

export const isQuestionAnswered = (question: AgentQuestion, answer?: AgentQuestionAnswer) => {
    if (!answer) return false;
    if (answer.skipped) return !question.required && !answer.text?.trim() && !answer.optionIds.length;
    if (answer.text?.trim()) return question.allowFreeText && answer.text.trim().length <= 4000 && !answer.optionIds.length;
    return (
        question.type !== 'text' &&
        answer.optionIds.length > 0 &&
        (question.type === 'multiple' || answer.optionIds.length === 1) &&
        new Set(answer.optionIds).size === answer.optionIds.length &&
        answer.optionIds.every((id) => question.options.some((option) => option.id === id))
    );
};

export const submissionEntry = (request: AgentTurnRequest): AgentChatEntry | null =>
    request.submission
        ? {
              id: `request-${request.requestId}`,
              role: 'merchant',
              requestId: request.requestId,
              cancelled: request.state === 'cancelled',
              text: request.submission.text,
              answers: request.submission.answers,
              attachments: (request.submission.attachments ?? []).map((attachment, index) => ({
                  ...attachment,
                  id: `request-${request.requestId}-attachment-${index}`,
                  previewUrl: attachment.url
              }))
          }
        : null;

export const restoreInteractionEntries = (conversation: AgentConversation): AgentChatEntry[] => {
    const requests = conversation.requests ?? [];
    const linkedTurns = new Set(requests.flatMap((request) => (request.turn?.turnId ? [request.turn.turnId] : [])));
    const entries: AgentChatEntry[] = [];
    const appendTurn = (turn: AgentConversation['turns'][number], index: number) => {
        entries.push({ id: `restored-${turn.turnId ?? index}`, role: 'agent', turn });
        entries.push(...publishedResultEntries(turn));
    };

    conversation.turns.filter((turn) => !turn.turnId || !linkedTurns.has(turn.turnId)).forEach(appendTurn);
    for (const request of requests) {
        const user = submissionEntry(request);
        if (user) entries.push(user);
        if (request.turn) appendTurn(request.turn, entries.length);
    }
    return entries;
};

export const recapToMessage = (recap: AgentAnswerRecap) =>
    'Clarification answers (preferences, not approval to apply changes):\n' +
    recap.answers
        .filter((answer) => !answer.skipped)
        .map((answer) => `${answer.question}: ${answer.text || answer.labels.join(', ')}`)
        .join('\n');
